import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Space, Typography } from "antd";
import { DataPoint } from "regression";
import { LineSeriesOption } from "echarts/charts";
import Chart, { EChartsOption } from "../../../components/Chart";
import { renderLabel } from "../../../libs/echar";
import {
  calcYAxis,
  createAuxiliaryData,
  createK,
  createQEFF2Data,
  createQH2Data,
  createQP,
  findIntersectionPoint,
  parseSpuPointsDataset,
} from "../../../utils";
import { PerformanceFormField, SpuPointsDataset, SpuPointsInfo } from "../../../stores/spuPoints";
import { DisplayUnits } from "../../SpuChart/displayUnits/definitions";
import {
  buildPerformanceBaseDisplayData,
  buildPerformanceDisplayModel,
} from "../../SpuChart/displayUnits/adapter";
import {
  formatAxisLabel,
  getDisplayUnitLabel,
} from "../../SpuChart/displayUnits/converters";
import { buildLeadingDashedSeries } from "../../SpuChart/leadingSegments";
import {
  buildBasePowerDisplayCurve,
  buildExtendedPowerDisplayCurve
} from "../../SpuChart/viscosity/powerCurve";
import { solveViscousAnchoredCurve } from "../../SpuChart/viscosity/corrections";
import { buildPerformanceChartDebugPayload } from "../../SpuChart/chartDebug";
import {
  boardCurveLabelStyle,
  createBoardGrid,
  createBoardTooltip,
  createBoardValueAxis,
  ndBoardTheme,
} from "../../SpuChart/boardTheme";
import "./PerformanceDebug.css";

const { Text, Title } = Typography;

type DebugViewMode = "composite" | "series-only" | "axis-only";
type CompareMode = "single" | "split";
type ThemeMode = "diagnostic" | "board";
type BaseProfileMode = "simplified" | "business";
type TraceMode = "compact" | "full";

interface LayerState {
  gridAxis: boolean;
  baseRaw: boolean;
  baseLeading: boolean;
  resultCurve: boolean;
  aux: boolean;
  marker: boolean;
  infoPanels: boolean;
}

interface LayeredCurveDebugChartProps {
  info: SpuPointsInfo;
  dataset: SpuPointsDataset;
  performance: PerformanceFormField;
  displayUnits: DisplayUnits;
}

const DEBUG_COLORS = {
  background: "#030712",
  grid: "#1f2937",
  minorGrid: "#111827",
  axis: "#d1d5db",
  text: "#f9fafb",
  rawQH: "#22d3ee",
  rawQEFF: "#fde047",
  rawQP: "#34d399",
  rawQNPSH: "#f472b6",
  leadingQH: "#38bdf8",
  leadingQEFF: "#fb7185",
  leadingQP: "#a78bfa",
  resultQH: "#ffffff",
  resultQEFF: "#f97316",
  resultQP: "#10b981",
  resultQNPSH: "#e879f9",
  aux: "#f43f5e",
  marker: "#60a5fa",
  infoPanelBorder: "#475569",
  infoPanelText: "#e5e7eb",
};

const BOARD_COLORS = {
  background: ndBoardTheme.background,
  text: ndBoardTheme.axisText,
  rawQH: ndBoardTheme.referenceCurve,
  rawQEFF: ndBoardTheme.referenceCurve,
  rawQP: ndBoardTheme.referenceCurve,
  rawQNPSH: ndBoardTheme.referenceCurve,
  leadingQH: ndBoardTheme.axisMuted,
  leadingQEFF: ndBoardTheme.axisMuted,
  leadingQP: ndBoardTheme.axisMuted,
  resultQH: ndBoardTheme.activeCurve,
  resultQEFF: ndBoardTheme.activeCurve,
  resultQP: ndBoardTheme.activeCurve,
  resultQNPSH: ndBoardTheme.activeCurve,
  aux: ndBoardTheme.auxiliaryCurve,
  marker: ndBoardTheme.marker,
  infoPanelBorder: ndBoardTheme.infoBorder,
  infoPanelText: ndBoardTheme.axisText,
};

const getThemeColors = (themeMode: ThemeMode) =>
  themeMode === "board" ? BOARD_COLORS : DEBUG_COLORS;

const renderToggleButtons = <T extends string>(
  value: T,
  onChange: (nextValue: T) => void,
  options: Array<{ label: string; value: T }>
) => (
  <Space.Compact>
    {options.map((option) => (
      <Button
        key={option.value}
        type={value === option.value ? "primary" : "default"}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </Button>
    ))}
  </Space.Compact>
);

const buildCompactChartTrace = (instance: any, extra: Record<string, any> = {}) => {
  const option = instance?.getOption?.() || {};
  const series = option?.series || [];
  const xAxis = option?.xAxis || [];
  const yAxis = option?.yAxis || [];
  const dom = instance?.getDom?.();
  const domCanvasSummary = dom
    ? Array.from(dom.querySelectorAll("canvas")).map((canvas: any, index: number) => ({
        index,
        width: canvas.width,
        height: canvas.height,
        styleWidth: canvas.style?.width,
        styleHeight: canvas.style?.height,
      }))
    : [];

  return {
    ...extra,
    explicitSeriesCount: series.length,
    optionSeries: series.map((item: any, index: number) => ({
      index,
      name: item?.name,
      type: item?.type,
      xAxisIndex: item?.xAxisIndex ?? 0,
      yAxisIndex: item?.yAxisIndex ?? 0,
      color: item?.lineStyle?.color || item?.itemStyle?.color || item?.color,
      lineWidth: item?.lineStyle?.width,
      lineDash: item?.lineStyle?.type,
      dataCount: Array.isArray(item?.data) ? item.data.length : 0,
    })),
    xAxisSummary: xAxis.map((axis: any, index: number) => ({
      index,
      name: axis?.name,
      type: axis?.type,
      splitNumber: axis?.splitNumber,
    })),
    yAxisSummary: yAxis.map((axis: any, index: number) => ({
      index,
      name: axis?.name,
      type: axis?.type,
      splitNumber: axis?.splitNumber,
    })),
    domCanvasSummary,
  };
};

const createAxis = ({
  name,
  gridIndex = 0,
  min,
  max,
  splitNumber = 10,
  showAxis = true,
  themeMode = "diagnostic",
}: {
  name: string;
  gridIndex?: number;
  min?: number;
  max?: number;
  splitNumber?: number;
  showAxis?: boolean;
  themeMode?: ThemeMode;
}) => ({
  gridIndex,
  type: "value" as const,
  name: showAxis ? name : "",
  min,
  max,
  splitNumber,
  nameTextStyle: {
    color: getThemeColors(themeMode).text,
    fontSize: 14,
    padding: [0, 0, 0, 0],
  },
  axisLine: {
    show: showAxis,
    lineStyle: {
      color: DEBUG_COLORS.axis,
      width: 1,
    },
  },
  axisTick: {
    show: showAxis,
    lineStyle: {
      color: DEBUG_COLORS.axis,
    },
  },
  axisLabel: {
    show: showAxis,
    color: getThemeColors(themeMode).text,
    fontSize: 12,
  },
  splitLine: {
    show: showAxis,
    lineStyle: {
      color: DEBUG_COLORS.grid,
      width: 1,
    },
  },
  minorTick: {
    show: showAxis,
    splitNumber: 4,
  },
  minorSplitLine: {
    show: showAxis,
    lineStyle: {
      color: DEBUG_COLORS.minorGrid,
      width: 1,
    },
  },
  silent: true,
});

const createBaseSeries = (
  displayDataSource: ReturnType<typeof buildPerformanceBaseDisplayData>,
  showLeading: boolean,
  themeMode: ThemeMode,
  baseProfileMode: BaseProfileMode
): LineSeriesOption[] => {
  const colors = getThemeColors(themeMode);
  let qeffIndex = 0;
  for (let i = 0; i < displayDataSource.QEFF.length; i += 1) {
    if ((displayDataSource.QEFF[i]?.[1] || 0) > 0) {
      qeffIndex = i;
      break;
    }
  }
  const useBusinessProfile = baseProfileMode === "business";
  const rawBaseSeries: LineSeriesOption[] = [
    {
      name: "QH1",
      type: "line",
      label: useBusinessProfile ? renderLabel("Q-H", [20, -8], undefined, boardCurveLabelStyle) : undefined,
      smooth: true,
      symbolSize: 0,
      lineStyle: { width: useBusinessProfile ? 1 : 2, color: colors.rawQH },
      color: colors.rawQH,
      data: displayDataSource.QH,
      animation: false,
      emphasis: { disabled: true },
    },
    {
      name: "QEFF",
      type: "line",
      label: useBusinessProfile
        ? renderLabel("Q-EFF", [18, -8], qeffIndex, boardCurveLabelStyle)
        : undefined,
      smooth: true,
      symbolSize: 0,
      lineStyle: { width: useBusinessProfile ? 1 : 2, color: colors.rawQEFF },
      color: colors.rawQEFF,
      data: displayDataSource.QEFF,
      yAxisIndex: 1,
      animation: false,
      emphasis: { disabled: true },
    },
    {
      name: "QP1",
      type: "line",
      label: useBusinessProfile ? renderLabel("Q-P", [18, -8], undefined, boardCurveLabelStyle) : undefined,
      smooth: true,
      symbolSize: 0,
      lineStyle: { width: useBusinessProfile ? 1 : 2, color: colors.rawQP },
      color: colors.rawQP,
      data: displayDataSource.QP,
      xAxisIndex: 1,
      yAxisIndex: 2,
      animation: false,
      emphasis: { disabled: true },
    },
    {
      name: "QNPSH",
      type: "line",
      label: useBusinessProfile ? renderLabel("Q-NPSHr", [20, -8], undefined, boardCurveLabelStyle) : undefined,
      smooth: true,
      symbolSize: 0,
      lineStyle: { width: useBusinessProfile ? 1 : 2, color: colors.rawQNPSH },
      color: colors.rawQNPSH,
      data: displayDataSource.QNPSH,
      xAxisIndex: 1,
      yAxisIndex: 3,
      animation: false,
      emphasis: { disabled: true },
    },
  ];

  if (!showLeading) {
    return rawBaseSeries;
  }

  const qhWrapped = buildLeadingDashedSeries(rawBaseSeries[0], displayDataSource.QH).map(
    (series, index) => ({
      ...series,
      color: index === 0 ? colors.leadingQH : colors.rawQH,
      lineStyle: {
        ...(series.lineStyle || {}),
        color: index === 0 ? colors.leadingQH : colors.rawQH,
      },
    })
  );
  const qeffWrapped = buildLeadingDashedSeries(rawBaseSeries[1], displayDataSource.QEFF).map(
    (series, index) => ({
      ...series,
      color: index === 0 ? colors.leadingQEFF : colors.rawQEFF,
      lineStyle: {
        ...(series.lineStyle || {}),
        color: index === 0 ? colors.leadingQEFF : colors.rawQEFF,
      },
    })
  );
  const qpWrapped = buildLeadingDashedSeries(rawBaseSeries[2], displayDataSource.QP).map(
    (series, index) => ({
      ...series,
      color: index === 0 ? colors.leadingQP : colors.rawQP,
      lineStyle: {
        ...(series.lineStyle || {}),
        color: index === 0 ? colors.leadingQP : colors.rawQP,
      },
    })
  );

  return [
    ...qhWrapped,
    ...qeffWrapped,
    ...qpWrapped,
    rawBaseSeries[3],
  ];
};

const buildResultSeriesModel = (
  info: SpuPointsInfo,
  dataset: SpuPointsDataset,
  performance: PerformanceFormField,
  displayUnits: DisplayUnits
) => {
  const { quantity, head, sg, viscosity, type = 0 } = performance;

  if (quantity === undefined || head === undefined) {
    return null;
  }

  const { QH, QP, QEFF, QNPSH } = parseSpuPointsDataset(dataset);
  const displayDataSource = buildPerformanceBaseDisplayData({ QH, QP, QEFF, QNPSH } as any, displayUnits);
  const displayBasePowerData = buildBasePowerDisplayCurve(displayDataSource.QP);
  const center = (info.range.start + info.range.end) / 2;
  const intersectionPoint = findIntersectionPoint(
    quantity,
    head,
    QH.equation,
    type,
    center
  );

  if (!intersectionPoint) {
    return null;
  }

  const auxData = createAuxiliaryData(quantity, head, 0, intersectionPoint[0], type);
  const k = createK(quantity, intersectionPoint[0], type);
  const qH2Points = createQH2Data(k, QH.points as DataPoint[], type);
  const qEFF2Points = createQEFF2Data(k, QEFF.points as DataPoint[], type);
  const qP2Points = createQP(qH2Points, qEFF2Points, sg || 1);

  const solvedCurve = solveViscousAnchoredCurve({
    quantity,
    head,
    sg,
    viscosityCanonical: viscosity,
    speed: info.speed,
    type,
    qhPoints: QH.dummyPoints,
    qeffPoints: QEFF.dummyPoints,
    qnpshPoints: QNPSH ? QNPSH.dummyPoints : [],
    qhRawPoints: QH.points as DataPoint[],
    qeffRawPoints: QEFF.points as DataPoint[],
    qhEquation: QH.equation,
    center,
  });

  if (!solvedCurve) {
    return null;
  }

  const finalQEFF2Data = solvedCurve.viscousCurves.qEFF2v.filter((point) => point[1] <= 100);
  const finalQP2Data = buildExtendedPowerDisplayCurve(
    displayBasePowerData,
    solvedCurve.viscousCurves.qP2vTrusted.length ? solvedCurve.viscousCurves.qP2vTrusted : qP2Points
  );

  return buildPerformanceDisplayModel({
    displayUnits,
    quantity,
    head,
    power: solvedCurve.correctedDutyPointResult.power,
    npsh: solvedCurve.correctedDutyPointResult.npsh ?? null,
    diameter: info.diameter,
    diameterUnit: "mm",
    auxData,
    qH2Data: solvedCurve.viscousCurves.qH2v,
    qEFF2Data: finalQEFF2Data,
    qNPSH2Data: solvedCurve.viscousCurves.qNPSH2v,
    qP2Data: finalQP2Data,
  });
};

const createResultSeries = (
  resultModel: ReturnType<typeof buildResultSeriesModel>,
  themeMode: ThemeMode
): LineSeriesOption[] => {
  if (!resultModel) {
    return [];
  }
  const colors = getThemeColors(themeMode);

  return [
    {
      name: "aux",
      type: "line",
      smooth: false,
      symbolSize: 0,
      lineStyle: { width: 2, type: "dashed", color: colors.aux },
      color: colors.aux,
      data: resultModel.auxData,
      animation: false,
      emphasis: { disabled: true },
    },
    {
      name: "QH2",
      type: "line",
      smooth: true,
      symbolSize: 0,
      lineStyle: { width: 2, color: colors.resultQH },
      color: colors.resultQH,
      data: resultModel.qH2Data,
      animation: false,
      emphasis: { disabled: true },
    },
    {
      name: "QEFF2",
      type: "line",
      smooth: true,
      symbolSize: 0,
      lineStyle: { width: 2, color: colors.resultQEFF },
      color: colors.resultQEFF,
      data: resultModel.qEFF2Data,
      yAxisIndex: 1,
      animation: false,
      emphasis: { disabled: true },
    },
    {
      name: "QP2",
      type: "line",
      smooth: true,
      symbolSize: 0,
      lineStyle: { width: 2, color: colors.resultQP },
      color: colors.resultQP,
      data: resultModel.qP2Data,
      xAxisIndex: 1,
      yAxisIndex: 2,
      animation: false,
      emphasis: { disabled: true },
    },
    {
      name: "QNPSH2",
      type: "line",
      smooth: true,
      symbolSize: 0,
      lineStyle: { width: 2, color: colors.resultQNPSH },
      color: colors.resultQNPSH,
      data: resultModel.qNPSH2Data,
      xAxisIndex: 1,
      yAxisIndex: 3,
      animation: false,
      emphasis: { disabled: true },
    },
  ];
};

const createMarkerSeries = (
  resultModel: ReturnType<typeof buildResultSeriesModel>,
  themeMode: ThemeMode
): any[] => {
  if (!resultModel) {
    return [];
  }
  const colors = getThemeColors(themeMode);

  return [
    {
      name: "intersection point",
      type: "line",
      data: [[resultModel.quantity, resultModel.head]],
      symbol: "circle",
      symbolSize: 12,
      lineStyle: { opacity: 0 },
      itemStyle: {
        color: colors.marker,
        borderColor: "#ffffff",
        borderWidth: 2,
      },
      animation: false,
    },
    {
      name: "intersection point",
      type: "line",
      xAxisIndex: 1,
      yAxisIndex: 2,
      data: [[resultModel.quantity, resultModel.power]],
      symbol: "circle",
      symbolSize: 8,
      lineStyle: { opacity: 0 },
      itemStyle: {
        color: colors.marker,
      },
      animation: false,
    },
    {
      name: "intersection point",
      type: "line",
      data: [[resultModel.quantity, resultModel.qEFF2Data.find((point) => Math.abs(point[0] - resultModel.quantity) < 0.5)?.[1] || 0]],
      yAxisIndex: 1,
      symbol: "circle",
      symbolSize: 8,
      lineStyle: { opacity: 0 },
      itemStyle: {
        color: colors.marker,
      },
      animation: false,
    },
  ];
};

const createInfoSeries = (
  resultModel: ReturnType<typeof buildResultSeriesModel>,
  performance: PerformanceFormField,
  displayUnits: DisplayUnits,
  themeMode: ThemeMode
): any[] => {
  if (!resultModel) {
    return [];
  }
  const colors = getThemeColors(themeMode);

  const topText = [
    `Q: ${resultModel.quantity.toFixed(1)} ${getDisplayUnitLabel("flow", displayUnits.flow)}`,
    `H: ${resultModel.head.toFixed(1)} ${getDisplayUnitLabel("head", displayUnits.head)}`,
    `P: ${resultModel.power.toFixed(2)} ${getDisplayUnitLabel("power", displayUnits.power)}`,
    `SG: ${performance.sg || 1}`,
  ].join("\n");

  const bottomText = [
    `NPSHr: ${(resultModel.npsh || 0).toFixed(2)} ${getDisplayUnitLabel("npshr", displayUnits.npshr)}`,
    `Viscosity: ${performance.viscosity ?? "-"} cSt`,
  ].join("\n");

  return [
    {
      name: "top info",
      type: "custom",
      silent: true,
      renderItem: (params: any, api: any) => {
        const coord = api.coord([resultModel.quantity, resultModel.head]);
        return {
          type: "group",
          children: [
            {
              type: "rect",
              shape: { x: coord[0] + 16, y: coord[1] - 110, width: 210, height: 92, r: 8 },
              style: {
                fill: "rgba(17,24,39,0.92)",
                stroke: colors.infoPanelBorder,
                lineWidth: 1,
              },
            },
            {
              type: "text",
              style: {
                x: coord[0] + 28,
                y: coord[1] - 92,
                text: topText,
                fill: colors.infoPanelText,
                font: "13px sans-serif",
                lineHeight: 20,
              },
            },
          ],
        };
      },
      data: [[resultModel.quantity, resultModel.head]],
    },
    {
      name: "bottom info",
      type: "custom",
      silent: true,
      xAxisIndex: 1,
      yAxisIndex: 2,
      renderItem: (params: any, api: any) => {
        const coord = api.coord([resultModel.quantity, resultModel.power]);
        return {
          type: "group",
          children: [
            {
              type: "rect",
              shape: { x: coord[0] + 16, y: coord[1] - 60, width: 220, height: 46, r: 8 },
              style: {
                fill: "rgba(17,24,39,0.92)",
                stroke: colors.infoPanelBorder,
                lineWidth: 1,
              },
            },
            {
              type: "text",
              style: {
                x: coord[0] + 28,
                y: coord[1] - 42,
                text: bottomText,
                fill: colors.infoPanelText,
                font: "13px sans-serif",
                lineHeight: 18,
              },
            },
          ],
        };
      },
      data: [[resultModel.quantity, resultModel.power]],
    },
  ];
};

const LayeredCurveDebugChart = ({
  info,
  dataset,
  performance,
  displayUnits,
}: LayeredCurveDebugChartProps) => {
  const [viewMode, setViewMode] = useState<DebugViewMode>("composite");
  const [compareMode, setCompareMode] = useState<CompareMode>("split");
  const [themeMode, setThemeMode] = useState<ThemeMode>("diagnostic");
  const [baseProfileMode, setBaseProfileMode] = useState<BaseProfileMode>("simplified");
  const [traceMode, setTraceMode] = useState<TraceMode>("compact");
  const [layers, setLayers] = useState<LayerState>({
    gridAxis: true,
    baseRaw: true,
    baseLeading: true,
    resultCurve: true,
    aux: true,
    marker: true,
    infoPanels: true,
  });
  const [rawTrace, setRawTrace] = useState<any>(null);
  const [wrappedTrace, setWrappedTrace] = useState<any>(null);

  const baseParsed = useMemo(() => {
    const parsed = parseSpuPointsDataset(dataset);
    const displayData = buildPerformanceBaseDisplayData(parsed as any, displayUnits);
    const resultModel = buildResultSeriesModel(info, dataset, performance, displayUnits);
    return { displayData, resultModel };
  }, [dataset, displayUnits, info, performance]);

  const axisRanges = useMemo(() => {
    const [hMax] = calcYAxis(baseParsed.displayData.QH.map((point) => point[1]), 10, 1);
    const [pMax] = calcYAxis(baseParsed.displayData.QP.map((point) => point[1]), 10, 1);
    return {
      flowMax: Math.max(...baseParsed.displayData.QH.map((point) => point[0]), 0) * 1.05,
      headMax: hMax,
      powerMax: pMax,
    };
  }, [baseParsed.displayData.QH, baseParsed.displayData.QP]);

  useEffect(() => {
    (window as any).__xzpPerformanceCurveDebug = {
      rawTrace,
      wrappedTrace,
      layers,
      viewMode,
      compareMode,
      themeMode,
      baseProfileMode,
      traceMode,
    };
  }, [baseProfileMode, compareMode, layers, rawTrace, themeMode, traceMode, viewMode, wrappedTrace]);

  const buildSeries = (variant: "raw" | "wrapped") => {
    if (viewMode === "axis-only") {
      return [];
    }

    const resultSeries = createResultSeries(baseParsed.resultModel, themeMode);
    const markerSeries = createMarkerSeries(baseParsed.resultModel, themeMode);
    const infoSeries = createInfoSeries(baseParsed.resultModel, performance, displayUnits, themeMode);
    const baseSeries =
      variant === "wrapped"
        ? createBaseSeries(baseParsed.displayData, true, themeMode, baseProfileMode)
        : createBaseSeries(baseParsed.displayData, false, themeMode, baseProfileMode);

    const nextSeries: any[] = [];

    if (layers.baseRaw && variant === "raw") {
      nextSeries.push(...baseSeries);
    }

    if (layers.baseLeading && variant === "wrapped") {
      nextSeries.push(...baseSeries);
    }

    if (layers.resultCurve) {
      nextSeries.push(...resultSeries.filter((series) => series.name !== "aux"));
    }

    if (layers.aux) {
      nextSeries.push(...resultSeries.filter((series) => series.name === "aux"));
    }

    if (layers.marker) {
      nextSeries.push(...markerSeries);
    }

    if (layers.infoPanels) {
      nextSeries.push(...infoSeries);
    }

    return nextSeries;
  };

  const buildOption = (variant: "raw" | "wrapped"): EChartsOption => {
    const showAxis = viewMode !== "series-only" && layers.gridAxis;
    const isBoard = themeMode === "board";

    return {
      backgroundColor: isBoard ? ndBoardTheme.background : DEBUG_COLORS.background,
      animation: false,
      tooltip: isBoard
        ? createBoardTooltip()
        : {
            trigger: "axis",
            backgroundColor: "rgba(15,23,42,0.96)",
            borderColor: DEBUG_COLORS.infoPanelBorder,
            textStyle: { color: DEBUG_COLORS.text },
          },
      grid: [
        isBoard
          ? createBoardGrid({ left: 56, right: 40, top: 32, bottom: "42%" })
          : { left: 56, right: 40, top: 32, bottom: "42%" },
        isBoard
          ? createBoardGrid({ left: 56, right: 40, top: "70%", bottom: 28 })
          : { left: 56, right: 40, top: "70%", bottom: 28 },
      ],
      xAxis: [
        isBoard
          ? createBoardValueAxis({
              name: showAxis ? formatAxisLabel("flow", displayUnits.flow) : "",
              min: 0,
              max: axisRanges.flowMax,
              splitNumber: 10,
              axisLabel: { show: showAxis },
              axisLine: { show: showAxis },
              axisTick: { show: showAxis },
              splitLine: { show: showAxis },
              minorTick: { show: showAxis },
              minorSplitLine: { show: showAxis },
            })
          : createAxis({
              name: formatAxisLabel("flow", displayUnits.flow),
              min: 0,
              max: axisRanges.flowMax,
              showAxis,
              themeMode,
            }),
        isBoard
          ? createBoardValueAxis({
              name: showAxis ? formatAxisLabel("flow", displayUnits.flow) : "",
              gridIndex: 1,
              min: 0,
              max: axisRanges.flowMax,
              splitNumber: 10,
              axisLabel: { show: showAxis },
              axisLine: { show: showAxis },
              axisTick: { show: showAxis },
              splitLine: { show: showAxis },
              minorTick: { show: showAxis },
              minorSplitLine: { show: showAxis },
            })
          : createAxis({
              name: formatAxisLabel("flow", displayUnits.flow),
              gridIndex: 1,
              min: 0,
              max: axisRanges.flowMax,
              showAxis,
              themeMode,
            }),
      ],
      yAxis: [
        isBoard
          ? createBoardValueAxis({
              name: showAxis ? formatAxisLabel("head", displayUnits.head) : "",
              min: 0,
              max: axisRanges.headMax,
              splitNumber: 5,
              axisLabel: { show: showAxis },
              axisLine: { show: showAxis },
              axisTick: { show: showAxis },
              splitLine: { show: showAxis },
              minorTick: { show: showAxis },
              minorSplitLine: { show: showAxis },
            })
          : createAxis({
              name: formatAxisLabel("head", displayUnits.head),
              min: 0,
              max: axisRanges.headMax,
              splitNumber: 5,
              showAxis,
              themeMode,
            }),
        isBoard
          ? createBoardValueAxis({
              name: showAxis ? formatAxisLabel("efficiency", displayUnits.efficiency) : "",
              min: 0,
              max: 120,
              splitNumber: 6,
              axisLabel: { show: showAxis },
              axisLine: { show: showAxis },
              axisTick: { show: showAxis },
              splitLine: { show: showAxis },
              minorTick: { show: showAxis },
              minorSplitLine: { show: showAxis },
            })
          : createAxis({
              name: formatAxisLabel("efficiency", displayUnits.efficiency),
              min: 0,
              max: 120,
              splitNumber: 6,
              showAxis,
              themeMode,
            }),
        isBoard
          ? createBoardValueAxis({
              name: showAxis ? formatAxisLabel("power", displayUnits.power) : "",
              gridIndex: 1,
              min: 0,
              max: axisRanges.powerMax,
              splitNumber: 5,
              axisLabel: { show: showAxis },
              axisLine: { show: showAxis },
              axisTick: { show: showAxis },
              splitLine: { show: showAxis },
              minorTick: { show: showAxis },
              minorSplitLine: { show: showAxis },
            })
          : createAxis({
              name: formatAxisLabel("power", displayUnits.power),
              gridIndex: 1,
              min: 0,
              max: axisRanges.powerMax,
              splitNumber: 5,
              showAxis,
              themeMode,
            }),
        isBoard
          ? createBoardValueAxis({
              name: showAxis ? formatAxisLabel("npshr", displayUnits.npshr) : "",
              gridIndex: 1,
              min: 0,
              max: 15,
              splitNumber: 5,
              axisLabel: { show: showAxis },
              axisLine: { show: showAxis },
              axisTick: { show: showAxis },
              splitLine: { show: showAxis },
              minorTick: { show: showAxis },
              minorSplitLine: { show: showAxis },
            })
          : createAxis({
              name: formatAxisLabel("npshr", displayUnits.npshr),
              gridIndex: 1,
              min: 0,
              max: 15,
              splitNumber: 5,
              showAxis,
              themeMode,
            }),
      ],
      series: buildSeries(variant),
    };
  };

  const traceSections = (trace: any) => {
    if (!trace) {
      return null;
    }

    const compactTrace = {
      explicitSeriesCount: trace.explicitSeriesCount,
      optionSeries: trace.optionSeries,
      xAxisSummary: trace.xAxisSummary,
      yAxisSummary: trace.yAxisSummary,
      domCanvasSummary: trace.domCanvasSummary,
      displayLineObjectCount: trace.displayLineObjectCount,
      slantedLineSegmentsByStroke: trace.slantedLineSegmentsByStroke,
      unmappedCurveDisplayObjects: trace.unmappedCurveDisplayObjects?.slice(0, 20),
    };

    if (traceMode === "compact") {
      return (
        <div className="performance-debug__trace">
          <div>
            <Text strong style={{ color: DEBUG_COLORS.text }}>Trace (Compact)</Text>
            <pre>{JSON.stringify(compactTrace, null, 2)}</pre>
          </div>
        </div>
      );
    }

    return (
      <div className="performance-debug__trace">
        <div>
          <Text strong style={{ color: DEBUG_COLORS.text }}>Series</Text>
          <pre>{JSON.stringify(trace.optionSeries, null, 2)}</pre>
        </div>
        <div>
          <Text strong style={{ color: DEBUG_COLORS.text }}>Axes</Text>
          <pre>{JSON.stringify({ xAxis: trace.xAxisSummary, yAxis: trace.yAxisSummary }, null, 2)}</pre>
        </div>
        <div>
          <Text strong style={{ color: DEBUG_COLORS.text }}>Chart Views</Text>
          <pre>{JSON.stringify(trace.chartViews, null, 2)}</pre>
        </div>
        <div>
          <Text strong style={{ color: DEBUG_COLORS.text }}>Canvas</Text>
          <pre>{JSON.stringify(trace.domCanvasSummary, null, 2)}</pre>
        </div>
        <div>
          <Text strong style={{ color: DEBUG_COLORS.text }}>Display Objects</Text>
          <pre>{JSON.stringify({
            displayLineObjectCount: trace.displayLineObjectCount,
            slantedLineSegmentsByStroke: trace.slantedLineSegmentsByStroke,
            unmappedCurveDisplayObjects: trace.unmappedCurveDisplayObjects?.slice(0, 20),
          }, null, 2)}</pre>
        </div>
      </div>
    );
  };

  return (
    <div className="performance-debug">
      <div className="performance-debug__toolbar">
        <Space wrap size={16}>
          <div>
            <Text strong>View</Text>
            <div style={{ marginLeft: 8, display: "inline-block" }}>
              {renderToggleButtons(viewMode, setViewMode, [
                { label: "Composite", value: "composite" },
                { label: "Series-only", value: "series-only" },
                { label: "Axis-only", value: "axis-only" },
              ])}
            </div>
          </div>
          <div>
            <Text strong>Compare</Text>
            <div style={{ marginLeft: 8, display: "inline-block" }}>
              {renderToggleButtons(compareMode, setCompareMode, [
                { label: "Single", value: "single" },
                { label: "Split", value: "split" },
              ])}
            </div>
          </div>
          <div>
            <Text strong>Theme</Text>
            <div style={{ marginLeft: 8, display: "inline-block" }}>
              {renderToggleButtons(themeMode, setThemeMode, [
                { label: "Diagnostic", value: "diagnostic" },
                { label: "Board", value: "board" },
              ])}
            </div>
          </div>
          <div>
            <Text strong>Base Profile</Text>
            <div style={{ marginLeft: 8, display: "inline-block" }}>
              {renderToggleButtons(baseProfileMode, setBaseProfileMode, [
                { label: "Simplified", value: "simplified" },
                { label: "Business", value: "business" },
              ])}
            </div>
          </div>
          <div>
            <Text strong>Trace</Text>
            <div style={{ marginLeft: 8, display: "inline-block" }}>
              {renderToggleButtons(traceMode, setTraceMode, [
                { label: "Compact", value: "compact" },
                { label: "Full", value: "full" },
              ])}
            </div>
          </div>
        </Space>
        <Checkbox.Group
          value={Object.entries(layers).filter(([, enabled]) => enabled).map(([key]) => key)}
          onChange={(values) => {
            const next = values as string[];
            setLayers({
              gridAxis: next.includes("gridAxis"),
              baseRaw: next.includes("baseRaw"),
              baseLeading: next.includes("baseLeading"),
              resultCurve: next.includes("resultCurve"),
              aux: next.includes("aux"),
              marker: next.includes("marker"),
              infoPanels: next.includes("infoPanels"),
            });
          }}
          options={[
            { label: "Grid / Axis", value: "gridAxis" },
            { label: "Base Raw", value: "baseRaw" },
            { label: "Base Leading", value: "baseLeading" },
            { label: "Result Curve", value: "resultCurve" },
            { label: "Aux", value: "aux" },
            { label: "Marker", value: "marker" },
            { label: "Info Panels", value: "infoPanels" },
          ]}
        />
      </div>

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="This page is a diagnostic renderer. It keeps the business calculation chain, but redraws the curve layers in a high-contrast theme so white artifacts are easier to isolate."
      />

      <div className={`performance-debug__charts performance-debug__charts--${compareMode}`}>
        <div className="performance-debug__chart-card">
          <Title level={5} style={{ color: DEBUG_COLORS.text }}>
            {compareMode === "split" ? "Raw Base Series" : "Diagnostic View"}
          </Title>
            <Chart
              className="performance-debug__chart"
              option={buildOption(compareMode === "split" ? "raw" : layers.baseLeading ? "wrapped" : "raw")}
              setOptionMode="clear-then-set"
              onChange={(instance) => {
                const extra = {
                  variant: compareMode === "split" ? "raw" : layers.baseLeading ? "wrapped" : "raw",
                  layers,
                  viewMode,
                };
                setRawTrace(
                  traceMode === "full"
                    ? buildPerformanceChartDebugPayload(instance, extra)
                    : buildCompactChartTrace(instance, extra)
                );
              }}
            />
          {traceSections(rawTrace)}
        </div>

        {compareMode === "split" ? (
          <div className="performance-debug__chart-card">
            <Title level={5} style={{ color: DEBUG_COLORS.text }}>
              Wrapped / Leading Series
            </Title>
            <Chart
              className="performance-debug__chart"
              option={buildOption("wrapped")}
              setOptionMode="clear-then-set"
              onChange={(instance) => {
                const extra = {
                  variant: "wrapped",
                  layers,
                  viewMode,
                };
                setWrappedTrace(
                  traceMode === "full"
                    ? buildPerformanceChartDebugPayload(instance, extra)
                    : buildCompactChartTrace(instance, extra)
                );
              }}
            />
            {traceSections(wrappedTrace)}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LayeredCurveDebugChart;
